<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MacroItem from './MacroItem.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { accountScopedUrl } = useAccount();
const { uiSettings } = useUISettings();

const query = ref('');
const closedGroups = ref({});

const macros = useMapGetter('macros/getMacros');
const uiFlags = useMapGetter('macros/getUIFlags');

const MACROS_ORDER_KEY = 'macros_display_order';

const categoryDefinitions = [
  {
    key: 'technical',
    title: 'واحد فنی',
    icon: 'i-lucide-monitor-cog',
    keywords: ['واحد فنی - '],
    order: [
      'رمز یکبار مصرف',
      'مشکل ورود',
      'سرور مجازی',
      'پلتفرم',
      'کپی ترید',
      'عدم نمایش حساب',
      'تغییر لول',
      'مشکل ایجاد حساب رویال',
    ],
  },
  {
    key: 'financial',
    title: 'واحد مالی',
    icon: 'i-lucide-wallet-cards',
    keywords: ['واحد مالی - '],
    order: ['واریز', 'برداشت', 'بونوس', 'ریبیت', 'مشکل پرداخت'],
  },
  {
    key: 'identity',
    title: 'واحد هویتی',
    icon: 'i-lucide-shield-check',
    keywords: ['واحد هویتی - '],
    order: [
      'احراز هویت',
      'تغییر ایمیل',
      'تغییر شماره موبایل',
      'حذف یا اصلاح کد ملی',
      'تغییر نام',
    ],
  },
  {
    key: 'crm',
    title: 'مدیر حساب',
    icon: 'i-lucide-users-round',
    keywords: ['مدیر حساب - '],
    order: [
      'موجودی صفر',
      'بونوس',
      'سرور مجازی',
      'حذف حساب',
      'کپی ترید',
      'تغییر ایمیل',
      'تغییر شماره موبایل',
    ],
  },
  {
    key: 'general',
    title: 'موارد دیگر',
    icon: 'i-lucide-layout-grid',
    keywords: ['موارد دیگر - '],
    order: ['سایر'],
  },
];

const orderedMacros = computed(() => {
  const savedOrder = uiSettings.value?.[MACROS_ORDER_KEY] ?? [];
  const currentMacros = macros.value ?? [];

  if (!savedOrder.length) {
    return currentMacros;
  }

  const orderMap = new Map(savedOrder.map((id, index) => [id, index]));

  return [...currentMacros].sort((a, b) => {
    return (orderMap.get(a.id) ?? Infinity) - (orderMap.get(b.id) ?? Infinity);
  });
});

const macroSearchText = macro => {
  const actionValues = (macro.actions ?? []).flatMap(action => {
    return Array.isArray(action.action_params) ? action.action_params : [];
  });

  return [macro.name, ...actionValues].join(' ').toLowerCase();
};

const resolveCategory = macro => {
  const searchable = macroSearchText(macro);

  return (
    categoryDefinitions.find(category =>
      category.keywords.some(keyword => searchable.includes(keyword))
    ) ?? categoryDefinitions.at(-1)
  );
};

const groupedMacros = computed(() => {
  const normalizedQuery = query.value.trim().toLowerCase();
  const groups = new Map();

  orderedMacros.value.forEach(macro => {
    if (normalizedQuery && !macroSearchText(macro).includes(normalizedQuery)) {
      return;
    }

    const category = resolveCategory(macro);

    if (!groups.has(category.key)) {
      groups.set(category.key, {
        ...category,
        macros: [],
      });
    }

    const prefix = category.keywords[0];
    groups.get(category.key).macros.push({
      ...macro,
      name: macro.name?.startsWith(prefix)
        ? macro.name.slice(prefix.length)
        : macro.name,
    });
  });

  groups.forEach(group => {
    const orderMap = new Map(group.order.map((name, index) => [name, index]));

    group.macros.sort(
      (a, b) =>
        (orderMap.get(a.name) ?? Number.MAX_SAFE_INTEGER) -
        (orderMap.get(b.name) ?? Number.MAX_SAFE_INTEGER)
    );
  });

  return categoryDefinitions
    .map(category => groups.get(category.key))
    .filter(Boolean);
});

const toggleGroup = key => {
  closedGroups.value[key] = !closedGroups.value[key];
};

const isGroupOpen = key => !closedGroups.value[key];

onMounted(() => {
  store.dispatch('macros/get');
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="orbex-macros-panel" dir="rtl">
    <div v-if="!uiFlags.isFetching && !macros.length" class="p-4">
      <p class="flex flex-col items-center justify-center h-full text-center">
        {{ $t('MACROS.LIST.404') }}
      </p>
      <router-link :to="accountScopedUrl('settings/macros')">
        <NextButton
          faded
          xs
          icon="i-lucide-plus"
          class="mt-1"
          :label="$t('MACROS.HEADER_BTN_TXT')"
        />
      </router-link>
    </div>

    <div
      v-if="uiFlags.isFetching"
      class="flex items-center gap-2 justify-center p-6 text-n-slate-12"
    >
      <span class="text-sm">{{ $t('MACROS.LOADING') }}</span>
      <Spinner class="size-5" />
    </div>

    <template v-if="!uiFlags.isFetching && macros.length">
      <div class="sticky top-0 z-10 px-2 pt-2 pb-1 orbex-macro-search-wrap">
        <div class="relative">
          <span
            class="i-lucide-search absolute right-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-9"
          />
          <input
            v-model="query"
            type="search"
            class="orbex-macro-search !mb-0 !pr-9"
            placeholder="جست‌وجوی ماکرو..."
          />
        </div>
      </div>

      <div class="flex flex-col gap-2 p-2">
        <section
          v-for="group in groupedMacros"
          :key="group.key"
          class="orbex-macro-group"
        >
          <button
            type="button"
            class="orbex-macro-group-header"
            @click="toggleGroup(group.key)"
          >
            <span class="flex items-center gap-2 min-w-0">
              <span
                class="size-4 flex-shrink-0 orbex-macro-category-icon"
                :class="group.icon"
              />
              <span class="font-semibold truncate">{{ group.title }}</span>
              <span class="orbex-macro-count">{{ group.macros.length }}</span>
            </span>

            <span
              class="size-4 text-n-slate-9"
              :class="
                isGroupOpen(group.key)
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
            />
          </button>

          <div v-show="isGroupOpen(group.key)" class="p-1.5 space-y-1">
            <MacroItem
              v-for="macro in group.macros"
              :key="macro.id"
              :macro="macro"
              :category="group"
              :conversation-id="conversationId"
            />
          </div>
        </section>

        <div
          v-if="!groupedMacros.length"
          class="p-5 text-sm text-center text-n-slate-10"
        >
          ماکرویی با این عبارت پیدا نشد.
        </div>
      </div>
    </template>
  </div>
</template>
